import React, { useState, useEffect } from 'react';
import { MultiSelect } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { getBillingProjects } from '../../../services/MasterAdmin/billingAnalyticsService';
import { opsPrimaryBtn, opsFilterClass, opsToolbarClass } from '../operations/operationsUi';

const GCP_SERVICES = [
  { value: 'services/F17B-412E-CB64', label: 'App Engine' },
  { value: 'services/149C-F9EC-3994', label: 'Artifact Registry' },
  { value: 'services/24E6-581D-38E5', label: 'BigQuery' },
  { value: 'services/8B5D-EF7D-EB12', label: 'Cloud Build' },
  { value: 'services/5490-F7B7-8DF6', label: 'Cloud Logging' },
  { value: 'services/58CD-E7C3-72CA', label: 'Cloud Monitoring' },
  { value: 'services/A1E8-BE35-7EBC', label: 'Cloud Pub/Sub' },
  { value: 'services/152E-C115-5142', label: 'Cloud Run' },
  { value: 'services/9662-B51E-5089', label: 'Cloud SQL' },
  { value: 'services/1F14-4801-0E16', label: 'Cloud Scheduler' },
  { value: 'services/95FF-2EF5-5EA1', label: 'Cloud Storage' },
  { value: 'services/C08E-37B9-80D3', label: 'Cloud Vision API' },
  { value: 'services/6F81-5844-456A', label: 'Compute Engine' },
  { value: 'services/B7D9-FDCB-15D8', label: 'Directions API' },
  { value: 'services/E1D8-71A6-53DE', label: 'Distance Matrix API' },
  { value: 'services/2662-232A-AC11', label: 'Firebase Hosting' },
  { value: 'services/AEFD-7695-64FA', label: 'Gemini API' },
  { value: 'services/0AC2-4B25-C429', label: 'Geocoding API' },
  { value: 'services/0161-1616-FF1D', label: 'Google Maps Platform - Routes' },
  { value: 'services/DC5D-D207-FD2F', label: 'Identity Platform' },
  { value: 'services/1934-AA85-20F9', label: 'Maps API' },
  { value: 'services/E505-1604-58F8', label: 'Networking' },
  { value: 'services/213C-9623-1402', label: 'Places API' },
  { value: 'services/3679-7487-549C', label: 'Roads API' },
  { value: 'services/EE82-7A5E-871C', label: 'Secret Manager' }
];

export const BillingFiltersBar = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [projectId, setProjectId] = useState(searchParams.get('projectId') || 'vedika-health');
  const [timeRange, setTimeRange] = useState(searchParams.get('timeRange') || 'This Month');
  const [customDates, setCustomDates] = useState<{start: string; end: string}>({
    start: searchParams.get('startDate') || '',
    end: searchParams.get('endDate') || ''
  });
  const [services, setServices] = useState<string[]>(
    searchParams.get('serviceId') ? searchParams.get('serviceId')!.split(',') : []
  );
  const [projectOptions, setProjectOptions] = useState<{value: string, label: string}[]>([]);

  useEffect(() => {
    getBillingProjects().then((data) => {
      if (data) {
        const options = data.map((p: any) => ({
          value: p.projectId,
          label: p.projectName || p.projectId
        }));
        setProjectOptions(options);
      }
    }).catch(console.error);
  }, []);

  const applyFilters = () => {
    const params = new URLSearchParams(searchParams);
    
    if (projectId) params.set('projectId', projectId);
    else params.delete('projectId');

    if (timeRange) params.set('timeRange', timeRange);
    else params.delete('timeRange');

    if (timeRange === 'Custom Range' && customDates.start && customDates.end) {
      params.set('startDate', customDates.start);
      params.set('endDate', customDates.end);
    } else {
      params.delete('startDate');
      params.delete('endDate');
    }

    if (services.length > 0) params.set('serviceId', services.join(','));
    else params.delete('serviceId');


    setSearchParams(params);
  };

  // Sync state if URL changes externally (e.g. back button)
  useEffect(() => {
    setProjectId(searchParams.get('projectId') || 'vedika-health');
    setTimeRange(searchParams.get('timeRange') || 'This Month');
    setServices(searchParams.get('serviceId') ? searchParams.get('serviceId')!.split(',') : []);
    
    if (searchParams.get('startDate') && searchParams.get('endDate')) {
      setCustomDates({
        start: searchParams.get('startDate') as string,
        end: searchParams.get('endDate') as string
      });
    } else {
      setCustomDates({ start: '', end: '' });
    }
  }, [searchParams]);

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      <div className={opsToolbarClass}>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className={opsFilterClass}
        >
          {projectOptions.length > 0 ? (
            projectOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)
          ) : (
            <option value={projectId}>{projectId}</option>
          )}
        </select>

        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className={opsFilterClass}
        >
          {['Yesterday', 'This Month', 'Last Month', 'Last 7 Days', 'Last 30 Days', 'Last 3 Months', 'Last 12 Months', 'Custom Range'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {timeRange === 'Custom Range' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customDates.start}
              onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
              className={opsFilterClass}
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={customDates.end}
              onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
              className={opsFilterClass}
            />
          </div>
        )}

        <MultiSelect
          placeholder="All Services"
          data={GCP_SERVICES}
          value={services}
          onChange={setServices}
          searchable
          clearable
          hidePickedOptions
          classNames={{
            input: opsFilterClass.replace('py-2', 'py-[3px]').replace('bg-white', 'bg-transparent'),
            root: 'min-w-[200px]',
          }}
          styles={{ input: { border: 'none' } }}
        />


        <button type="button" className={opsPrimaryBtn} onClick={applyFilters}>
          Apply Filters
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        BigQuery billing export is available from 6 Jul 2026 onwards. Earlier dates are not shown.
      </p>
    </div>
  );
};
